/**
 * ParsedTicketsProvider — parses the enriched SIM ticket export ONCE, above the Tickets tabs,
 * and shares the result via context.
 *
 * WHY: enrichedListToParsed runs ~5 moment() ops per ticket (~20k+ across the dataset). Previously
 * each tab's hook (useSimHealthPreview, useSimOeDashboard, TimeSpentDetailsTab) parsed the full
 * set independently, and some did so inside a memo keyed on the active filters — so every filter
 * change (and every kept-alive tab) re-parsed everything on the main thread, freezing the UI on
 * large ranges. Parsing here, memoized on `enriched.tickets` alone, means:
 *   - parse happens ONCE per data load (not per filter change, not per tab),
 *   - tabs can unmount freely (parsed data + fetch state live here) — no keepAlive needed,
 *   - each tab only does the cheap applySimTicketFilters on the shared parsed array.
 */
import React, { createContext, useContext, useMemo } from 'react';
import moment from 'moment-timezone';
import { TicketsParsedDataItem } from '../../tickets/TicketsInterfaces';
import { DashboardInsight, EnrichedTicket, OeOpportunity } from '../../sim-ticket-health-preview/interfaces/enrichedTicketExport.types';
import { enrichedListToParsed, FirstResponseByCaseId } from '../../sim-ticket-health-preview/utils/enrichedAdapter';
import { useEnrichedTickets } from '../../sim-ticket-health-preview/hooks/useEnrichedTickets';
import { useTicketsData } from '../hooks/useTicketsData';

export interface ParsedTicketsContextValue {
  status: 'loading' | 'finished' | 'error';
  /** The full enriched population, parsed to TicketsParsedDataItem — parsed ONCE. */
  parsedAll: TicketsParsedDataItem[];
  /** Raw enriched tickets indexed by case_id (fallback sim_issue_alias) for LLM-overlay lookups. */
  byId: Map<string, EnrichedTicket>;
  /** Stable "now" used for parsing/scoring, so age/derived values don't drift between reads. */
  asOf: moment.Moment;
  oeOpportunities: OeOpportunity[];
  dashboardInsights: Record<string, DashboardInsight>;
  generatedAt: string | undefined;
  lastUpdatedAt: string | null;
}

const EMPTY: ParsedTicketsContextValue = {
  status: 'loading',
  parsedAll: [],
  byId: new Map(),
  asOf: moment(0),
  oeOpportunities: [],
  dashboardInsights: {},
  generatedAt: undefined,
  lastUpdatedAt: null
};

const ParsedTicketsContext = createContext<ParsedTicketsContextValue>(EMPTY);

export const ParsedTicketsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const enriched = useEnrichedTickets();
  // TEMPORARY bridge: the enriched export does not carry first-response inputs, so load the raw
  // sim_tickets export too, purely to recover first_response_age_in_seconds per case_id for the
  // Sev-2 first-response SLA. Remove once the pipeline adds first_human_correspondence_date to the
  // enriched export. The raw load is non-blocking — if it is absent/slow, the map is empty and the
  // field stays null (N/A), i.e. the pre-bridge behaviour.
  const raw = useTicketsData();

  // case_id → first_response_age_in_seconds, from the raw parsed tickets (which already computed it).
  const firstResponseByCaseId = useMemo<FirstResponseByCaseId>(() => {
    const map: FirstResponseByCaseId = new Map();
    const rawTickets = raw.data?.data ?? [];
    rawTickets.forEach((t) => {
      if (t.case_id) map.set(t.case_id, t.first_response_age_in_seconds ?? null);
    });
    return map;
  }, [raw.data]);

  const value = useMemo<ParsedTicketsContextValue>(() => {
    const asOf = moment();
    // Parse + index ONCE, keyed on the ticket data only (NOT on filters).
    const parsedAll = enriched.tickets.length > 0 ? enrichedListToParsed(enriched.tickets, asOf, firstResponseByCaseId) : [];
    const byId = new Map(enriched.tickets.map((t) => [t.case_id || t.sim_issue_alias, t]));
    return {
      status: enriched.status,
      parsedAll,
      byId,
      asOf,
      oeOpportunities: enriched.oeOpportunities,
      dashboardInsights: enriched.dashboardInsights,
      generatedAt: enriched.generatedAt,
      lastUpdatedAt: enriched.lastUpdatedAt
    };
    // Re-parse ONLY when the underlying ticket data (or load status) changes — never on filters.
  }, [
    enriched.status,
    enriched.tickets,
    enriched.oeOpportunities,
    enriched.dashboardInsights,
    enriched.generatedAt,
    enriched.lastUpdatedAt,
    firstResponseByCaseId
  ]);

  return <ParsedTicketsContext.Provider value={value}>{children}</ParsedTicketsContext.Provider>;
};

/** Consume the once-parsed enriched ticket population. */
export const useParsedEnrichedTickets = (): ParsedTicketsContextValue => useContext(ParsedTicketsContext);
